/**
 * Client-side validation matching the Smithy model constraints from
 * LangHubServiceModel/model/string/operations.smithy (DescribeStringRequest).
 */

const ID_PATTERN = /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9_-]+$/;
const CX_ID_PATTERN = /^[a-z0-9.-]+$/;

export interface ValidationError {
  field: string;
  message: string;
}

export function validateStringDetailParams(params: {
  namespaceId?: string;
  stringGroupId?: string;
  stringId?: string;
  variant?: string | null;
  version?: string | null;
}): ValidationError[] {
  const errors: ValidationError[] = [];

  // namespaceId: required, 1-200 chars, pattern
  if (!params.namespaceId) {
    errors.push({ field: 'namespaceId', message: 'Namespace ID is required.' });
  } else if (params.namespaceId.length > 200) {
    errors.push({
      field: 'namespaceId',
      message: 'Namespace ID must be at most 200 characters.',
    });
  } else if (!ID_PATTERN.test(params.namespaceId)) {
    errors.push({
      field: 'namespaceId',
      message:
        'Namespace ID must contain only letters, digits, hyphens, or underscores and include at least one alphanumeric character.',
    });
  }

  // stringGroupId: required, 1-175 chars, same pattern
  if (!params.stringGroupId) {
    errors.push({
      field: 'stringGroupId',
      message: 'String Group ID is required.',
    });
  } else if (params.stringGroupId.length > 175) {
    errors.push({
      field: 'stringGroupId',
      message: 'String Group ID must be at most 175 characters.',
    });
  } else if (!ID_PATTERN.test(params.stringGroupId)) {
    errors.push({
      field: 'stringGroupId',
      message:
        'String Group ID must contain only letters, digits, hyphens, or underscores and include at least one alphanumeric character.',
    });
  }

  // stringId: required, 1-200 chars, same pattern
  if (!params.stringId) {
    errors.push({ field: 'stringId', message: 'String ID is required.' });
  } else if (params.stringId.length > 200) {
    errors.push({
      field: 'stringId',
      message: 'String ID must be at most 200 characters.',
    });
  } else if (!ID_PATTERN.test(params.stringId)) {
    errors.push({
      field: 'stringId',
      message:
        'String ID must contain only letters, digits, hyphens, or underscores and include at least one alphanumeric character.',
    });
  }

  // variant (cxId): optional; if present must be 1-256 chars, pattern ^[a-z0-9.-]+$
  if (params.variant !== null && params.variant !== undefined) {
    if (params.variant.length === 0) {
      errors.push({
        field: 'variant',
        message: 'Variant CX ID must not be empty if provided.',
      });
    } else if (params.variant.length > 256) {
      errors.push({
        field: 'variant',
        message: 'Variant CX ID must be at most 256 characters.',
      });
    } else if (!CX_ID_PATTERN.test(params.variant)) {
      errors.push({
        field: 'variant',
        message:
          'Variant CX ID must contain only lowercase letters, digits, dots, or hyphens.',
      });
    }
  }

  // version (stringGroupVersion): optional; if present must be a positive integer >= 1
  // Matches Smithy constraint: @range(min: 1) on stringGroupVersion: Long
  if (params.version !== null && params.version !== undefined) {
    const parsed = Number(params.version);
    if (!Number.isFinite(parsed) || !Number.isInteger(parsed) || parsed < 1) {
      errors.push({
        field: 'version',
        message: 'Version must be a positive integer (1 or greater).',
      });
    }
  }

  return errors;
}
